import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { Tag } from "../../../../design-system";
import {
  TONE_DOT,
  humanizeStage,
} from "../../outreach/components/leadUtils";
import {
  AREA_LABELS,
  BoardCard,
  BoardCardType,
  BoardPriority,
  BoardStageDef,
} from "../../../../types/Board";

// The global issue kanban. Structural copy of the outreach LeadBoard (native
// HTML5 drag and drop, no library): columns are the editable stages, cards are
// bugs / feature requests / improvements. Dragging is gated by canMove so
// coach admins get a read-and-click board while operators re-stage cards.

const BoardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-3);
`;

const Hint = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin: 0;
`;

const Scroller = styled.div`
  display: flex;
  gap: var(--space-3);
  overflow-x: auto;
  padding-bottom: var(--space-3);
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: var(--radius-full);
  }
`;

const Column = styled.div<{ $over: boolean }>`
  flex: 0 0 268px;
  width: 268px;
  display: flex;
  flex-direction: column;
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  transition: border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);

  ${(p) =>
    p.$over &&
    css`
      border-color: var(--color-accent);
      box-shadow: 0 0 0 1px var(--color-accent);
    `}
`;

const ColumnHead = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: var(--space-3);
  background: var(--color-surface-subtle);
  border-bottom: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  z-index: 1;
`;

const ColumnTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Dot = styled.span<{ $c: string }>`
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  background: ${(p) => p.$c};
  flex-shrink: 0;
`;

const ColumnCount = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-tertiary);
  background: var(--color-surface-inset);
  padding: 1px var(--space-2);
  border-radius: var(--radius-full);
`;

const ColumnBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3);
  min-height: 56px;
  max-height: calc(100vh - 340px);
  overflow-y: auto;
  flex: 1;
`;

const CardEl = styled.div<{ $dragging: boolean; $grab: boolean }>`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: ${(p) => (p.$grab ? "grab" : "pointer")};
  box-shadow: var(--shadow-xs);
  transition: border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);
  opacity: ${(p) => (p.$dragging ? 0.4 : 1)};

  &:hover {
    border-color: var(--color-accent);
    box-shadow: var(--shadow-sm);
  }
  ${(p) =>
    p.$grab &&
    css`
      &:active {
        cursor: grabbing;
      }
    `}
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
`;

const CardTitle = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const CardMeta = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardFoot = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
`;

const PriorityBadge = styled.span<{ $p: BoardPriority }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  padding: 1px var(--space-2);
  border-radius: var(--radius-full);
  ${(p) =>
    p.$p === "urgent"
      ? css`
          color: var(--color-danger);
          background: var(--color-danger-subtle, var(--color-surface-inset));
        `
      : p.$p === "high"
      ? css`
          color: var(--color-warning);
          background: var(--color-warning-subtle, var(--color-surface-inset));
        `
      : p.$p === "medium"
      ? css`
          color: var(--color-text-secondary);
          background: var(--color-surface-inset);
        `
      : css`
          color: var(--color-text-tertiary);
          background: var(--color-surface-inset);
        `}
`;

const CountBadge = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-left: auto;
`;

const Empty = styled.div<{ $over: boolean }>`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  text-align: center;
  padding: var(--space-4) var(--space-2);
  border: 1px dashed
    ${(p) => (p.$over ? "var(--color-accent)" : "var(--color-border)")};
  border-radius: var(--radius-md);
`;

const TYPE_VARIANT: Record<BoardCardType, "danger" | "accent" | "neutral"> = {
  bug: "danger",
  feature: "accent",
  improvement: "neutral",
};

export const TYPE_LABELS: Record<BoardCardType, string> = {
  bug: "Bug",
  feature: "Feature",
  improvement: "Improvement",
};

// ─── card ───────────────────────────────────────────────────────────────────

const IssueCard: React.FC<{
  card: BoardCard;
  dragging: boolean;
  canMove: boolean;
  onSelect: (card: BoardCard) => void;
  onDragStart: (card: BoardCard) => void;
  onDragEnd: () => void;
}> = ({ card, dragging, canMove, onSelect, onDragStart, onDragEnd }) => (
  <CardEl
    $dragging={dragging}
    $grab={canMove}
    draggable={canMove}
    onClick={() => onSelect(card)}
    onDragStart={(e) => {
      if (!canMove) return;
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", card.id);
      onDragStart(card);
    }}
    onDragEnd={onDragEnd}
  >
    <CardTop>
      <Tag variant={TYPE_VARIANT[card.type]} size="sm">
        {TYPE_LABELS[card.type]}
      </Tag>
      {/* A coach sent this in through their MCP connection and is waiting on
          an answer, so it reads differently from our own board notes. */}
      {card.source === "coach" && (
        <Tag variant={card.reviewedAt ? "neutral" : "warning"} size="sm">
          {card.reviewedAt ? "coach" : "new · coach"}
        </Tag>
      )}
      <PriorityBadge $p={card.priority}>{card.priority}</PriorityBadge>
    </CardTop>
    <CardTitle>{card.title}</CardTitle>
    <CardMeta>
      {[
        AREA_LABELS[card.area],
        card.creatorCommunityName || card.creatorCommunityId,
        card.source === "coach"
          ? card.submittedBy?.name || card.submittedBy?.email || "coach"
          : card.createdBy.fullName,
      ]
        .filter(Boolean)
        .join(" · ") || "—"}
    </CardMeta>
    {card.attachments.length > 0 && (
      <CardFoot>
        <CountBadge>
          {card.attachments.length} attachment
          {card.attachments.length === 1 ? "" : "s"}
        </CountBadge>
      </CardFoot>
    )}
  </CardEl>
);

// ─── board ──────────────────────────────────────────────────────────────────

const IssueBoard: React.FC<{
  stages: BoardStageDef[];
  cards: BoardCard[];
  byStage: Record<string, BoardCard[]>;
  canMove: boolean;
  onSelect: (card: BoardCard) => void;
  onMoveCard: (card: BoardCard, stage: string, position: number) => void;
}> = ({ stages, cards, byStage, canMove, onSelect, onMoveCard }) => {
  const [dragging, setDragging] = useState<BoardCard | null>(null);
  const [overStage, setOverStage] = useState<string | null>(null);

  const stageByKey = useMemo(() => {
    const map: Record<string, BoardStageDef> = {};
    stages.forEach((s) => (map[s.key] = s));
    return map;
  }, [stages]);

  // Columns = configured stages, then any "orphan" statuses still present on
  // cards after a stage was renamed/deleted, appended so no card disappears.
  const columns = useMemo(() => {
    const orphans = Array.from(new Set(cards.map((c) => c.status))).filter(
      (k) => k && !stageByKey[k]
    );
    return [...stages.map((s) => s.key), ...orphans];
  }, [cards, stages, stageByKey]);

  const isOrphan = (key: string) => !stageByKey[key];
  const label = (key: string) => stageByKey[key]?.label || humanizeStage(key);
  const dot = (key: string) =>
    TONE_DOT[stageByKey[key]?.tone || "neutral"] || TONE_DOT.neutral;

  const endDrag = () => {
    setDragging(null);
    setOverStage(null);
  };

  const handleDrop = (stage: string) => {
    if (dragging && !isOrphan(stage)) {
      const items = (byStage[stage] || []).filter((c) => c.id !== dragging.id);
      // Drop appends to the bottom of the target column (sparse positions).
      const position = items.length
        ? items[items.length - 1].position + 1000
        : Date.now();
      if (dragging.status !== stage) onMoveCard(dragging, stage, position);
    }
    endDrag();
  };

  return (
    <>
      {canMove && (
        <BoardHead>
          <Hint>Drag a card to another column to change its state.</Hint>
        </BoardHead>
      )}
      <Scroller>
        {columns.map((stage) => {
          const items = byStage[stage] || [];
          const orphan = isOrphan(stage);
          const over = overStage === stage && !orphan;
          return (
            <Column
              key={stage}
              $over={over}
              onDragOver={(e) => {
                if (!dragging || orphan) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                if (overStage !== stage) setOverStage(stage);
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node))
                  setOverStage((s) => (s === stage ? null : s));
              }}
              onDrop={(e) => {
                e.preventDefault();
                handleDrop(stage);
              }}
            >
              <ColumnHead>
                <ColumnTitle>
                  <Dot
                    $c={orphan ? "var(--color-text-tertiary)" : dot(stage)}
                  />
                  {label(stage)}
                  {orphan && (
                    <Tag variant="neutral" size="sm">
                      archived
                    </Tag>
                  )}
                </ColumnTitle>
                <ColumnCount>{items.length}</ColumnCount>
              </ColumnHead>
              <ColumnBody>
                {items.length === 0 ? (
                  <Empty $over={over}>{over ? "Drop here" : "—"}</Empty>
                ) : (
                  items.map((c) => (
                    <IssueCard
                      key={c.id}
                      card={c}
                      dragging={dragging?.id === c.id}
                      canMove={canMove}
                      onSelect={onSelect}
                      onDragStart={setDragging}
                      onDragEnd={endDrag}
                    />
                  ))
                )}
              </ColumnBody>
            </Column>
          );
        })}
      </Scroller>
    </>
  );
};

export default IssueBoard;
export { IssueBoard, TYPE_VARIANT };
